import React from 'react';
import 'twin.macro';
import {
  Periode,
  Matiere as IMatiere,
  Item,
} from '@teetsh/app/src/types/programmations';
import { HeaderItem } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/components/HeaderItem';
import { MatiereLayout } from './components/MatiereLayout';
import { IRow, Row } from './components/Row';
import _orderBy from '@teetsh/app/src/utils/arrays/orderBy';
import { shortenPeriodeLabel } from '@teetsh/app/src/containers/Programmations/ProgV2/Main/Periodes/shortenPeriodeLabel';
import { useTranslation } from 'react-i18next';
import { RowTitleOrientation } from '@teetsh/app/src/containers/Programmations/ProgV2/Print/types';
import { Filler, Header, IHeader } from './types';
import { getPureReferentialItemName } from '@teetsh/app/src/components/Bo/ReferentialItemElement';

export function buildRows(
  matiere: IMatiere,
  periodes: Periode[],
  enforceNbColumns?: number,
): IRow[] {
  return matiere.domaines.map(d => {
    const blocks = periodes.map(p => ({
      items: _orderBy(
        d.periodContents
          .filter(pc => pc.periodeId === p.id)
          .flatMap(v => v.items),
        'y',
        ['asc'],
      ),
    }));

    const additionalBlocks = enforceNbColumns
      ? Array.from<{ items: Item[] }>({
          length: enforceNbColumns - periodes.length,
        }).fill({ items: [] })
      : [];

    return {
      header: {
        color: d.color,
        name: getPureReferentialItemName(d.name),
      },
      subrows: [
        {
          blocks: [...blocks, ...additionalBlocks],
        },
      ],
    };
  });
}

function buildHeaders(
  periodes: Periode[],
  enforceNbColumns?: number,
): IHeader[] {
  const headers: Header[] = periodes.map(p => ({
    type: 'header',
    name: p.name,
    color: p.color,
    subtitle: p.subtitle,
  }));
  const fillers = enforceNbColumns
    ? Array.from<Filler>({ length: enforceNbColumns - periodes.length }).fill({
        type: 'filler',
      })
    : [];
  return [...headers, ...fillers];
}

interface Props {
  matiere: IMatiere;
  periodes: Periode[];
  enforceNbColumns?: number;
  periodeLabel?: string;
  matiereTitle?: string;
  rowTitleOrientation: RowTitleOrientation;
  isFirst: boolean;
  preventCutting: boolean;
  breakBefore: boolean;
}

export function PeriodeDomaineView(props: Props) {
  const { matiere, periodes, enforceNbColumns, periodeLabel } = props;
  const { t } = useTranslation(['common', 'programmation']);
  const hasPeriodeSubtitles = periodes.some(p => p.subtitle);
  const rows = buildRows(matiere, periodes, enforceNbColumns);
  const headers = buildHeaders(periodes, enforceNbColumns);

  const areRowsEmpty = rows.every(r =>
    r.subrows.every(s =>
      (s.blocks || []).every(b => (b?.items || []).length === 0),
    ),
  );
  // Dans le cas d'un premier groupe même si c'est vide on affiche quand même
  // la structure, ça semble mieux que juste du vide
  if (areRowsEmpty && !props.isFirst) {
    return null;
  }

  return (
    <MatiereLayout
      sectionTitle={periodeLabel}
      shortSectionTitle={shortenPeriodeLabel({ label: periodeLabel, t })}
      matiereTitle={props.matiereTitle}
      rowTitleOrientation={props.rowTitleOrientation}
      breakBefore={props.breakBefore && !props.isFirst}
    >
      {headers.map((p, idx) => {
        if (p.type === 'filler') {
          return <div key={idx} tw="flex-1 min-w-0" />;
        }
        return (
          <div key={idx} tw="flex-1 min-w-0">
            <HeaderItem
              value={p.name}
              color={p.color}
              details={p.subtitle}
              shouldStretch={hasPeriodeSubtitles}
            />
          </div>
        );
      })}
      {rows.map((r, idx) => (
        <Row
          rowTitleOrientation={props.rowTitleOrientation}
          key={idx}
          subrows={r.subrows}
          header={r.header}
          preventCutting={props.preventCutting}
          breakBefore={props.breakBefore}
        />
      ))}
    </MatiereLayout>
  );
}
